import React, { ReactElement, useState, useEffect, useMemo } from "react";
import { makeStyles, Button, IconButton, Dialog, CircularProgress } from "@material-ui/core";
import theme from "../../../themes";
import HelpIcon from "../../../images/help.png";
import LanguageIcon from "../../../images/language.png";
import LogoutIcon from "../../../images/logout.png";
import UploadIcon from "../../../images/upload-rounded.png";
import PlayIcon from "../../../images/play.png";
import EditOrangeIcon from "../../../images/edit-icon-orange.png";
import TrashIcon from "../../../images/trash.png";
import InformationIcon from "../../../images/information-outline.png";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import CloseIcon from "@material-ui/icons/Close";
import BannerUploadModal from "../ChamberAdmin/BannerUploadModal";
import DeleteBannerModal from "../ChamberAdmin/DeleteBannerModal";
import useIsMemberProfileAndPerksBannersActiveForChamberMember from "../../common/Hooks/useCheckIfMemberProfileAndPerksBannersActiveForChamberMember";
import { useHistory } from "react-router-dom";
import useGetChamberMemberFromUserIdInAuth from "../../common/Hooks/useGetChamberMemberFromUserIdInAuth";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import {
  BannerContentTypeEnum,
  useGetChamberMemberQuery,
  useUpdateChamberMemberMutation,
} from "../../../graphql/types";
import omitDeep from "omit-deep-lodash";
import { toast } from "react-toastify";
import VideoTutorialButton from "../../common/VideoTutorialButton";
import ExportButton from "../../common/ExportButton";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { openInstructionPdf } from "../../common/openInstructionPdf";
import FeaturedMemberBannerGuidance from "../../common/FeaturedMemberBannerGuidance";

const useStyles = makeStyles((themes: any) => ({
  root: {
    backgroundColor: "#E1A731",
    minHeight: "100vh",
    paddingTop: "20px",
    fontFamily: "DM Sans, sans-serif",
    overflowX: "hidden",
  },
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginLeft: "24px",
    marginRight: "24px",
    marginTop: "15px",
    marginBottom: "30px",
    [theme.breakpoints.down("sm")]: {
      marginLeft: "12px",
      marginRight: "12px",
    },
  },
  title: {
    marginLeft: "0",
    fontSize: "30px",
    color: "white",
    fontWeight: "bold",
    display: "flex",
    margin: 0,
    fontFamily: "DM Sans, sans-serif",
    [theme.breakpoints.down("sm")]: {
      fontSize: "22px",
    },
  },
  headerIcons: {
    display: "flex",
    gap: "15px",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  headerIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  headerLine: {
    borderTop: "2px dotted white",
    marginLeft: "24px",
    marginRight: "24px",
    marginBottom: "20px",
    [theme.breakpoints.down("sm")]: {
      marginLeft: "12px",
      marginRight: "12px",
    },
  },
  description: {
    color: "white",
    marginLeft: "24px",
    marginRight: "24px",
    fontSize: "16px",
    lineHeight: "1.5",
    marginBottom: "20px",
    fontFamily: "DM Sans, sans-serif",
    [theme.breakpoints.down("sm")]: {
      marginLeft: "12px",
      marginRight: "12px",
      fontSize: "16px",
      lineHeight: "1.55",
    },
  },
  actionRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginLeft: "24px",
    marginRight: "24px",
    marginBottom: "30px",
    [theme.breakpoints.down("sm")]: {
      marginLeft: "12px",
      marginRight: "12px",
    },
  },
  videoButton: {
    backgroundColor: "white",
    color: "#E1A731",
    padding: "10px 24px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "bold",
    textTransform: "none",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#f9f9f9",
    },
  },
  content: {
    background: "#FFFFFF",
    flexGrow: 1,
    borderRadius: "16px",
    margin: "20px",
    flex: "1",
    marginTop: "33px",
    paddingTop: "30px",
    overflowX: "hidden",
    [theme.breakpoints.down("sm")]: {
      margin: "8px",
      marginTop: "16px",
      paddingTop: "20px",
    },
  },
  sectionHeader: {
    backgroundColor: "#E1A731",
    borderRadius: "25px",
    padding: "10px 30px",
    display: "inline-block",
    position: "relative",
    top: "-15px",
    left: "30px",
    [theme.breakpoints.down("sm")]: {
      left: "0",
      padding: "8px 16px",
    },
  },
  sectionTitle: {
    color: "white",
    fontSize: "16px",
    fontWeight: 600,
    margin: 0,
  },
  sectionHelperText: {
    color: "#000000",
    fontSize: "16px",
    margin: "20px 30px 15px 30px",
    [theme.breakpoints.down("sm")]: {
      margin: "12px 0 8px 0",
    },
  },
  sectionHeader2: {
    color: "#666",
    fontSize: "13px",
    margin: "15px 30px 25px 30px",
    lineHeight: "1.6",
    [theme.breakpoints.down("sm")]: {
      margin: "8px 0 12px 0",
    },
  },
  formSection: {
    maxWidth: "1040px",
    margin: "30px auto 30px auto",
    padding: "0px 30px 30px 30px",
    border: "2px dotted #CCC",
    borderRadius: "8px",
    position: "relative",
    boxSizing: "border-box" as any,
    [theme.breakpoints.down("sm")]: {
      padding: "0px 12px 16px 12px",
    },
  },
  sectionWrapper: {
    position: "relative",
    marginTop: "30px",
  },
  infoIcon: {
    position: "absolute",
    top: "-15px",
    right: "-10px",
    width: "24px",
    height: "24px",
    cursor: "pointer",
    opacity: 0.6,
    "&:hover": {
      opacity: 1,
    },
  },
  sectionHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "0px",
  },
  uploadGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr 1fr",
    gap: "20px",
    marginTop: "20px",
    marginBottom: "30px",
    width: "100%",
    boxSizing: "border-box" as any,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr 1fr",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr 1fr",
      gap: "12px",
    },
  },
  bannerCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: "10px",
    overflow: "hidden",
    border: "2px solid #E1A731",
    display: "flex",
    flexDirection: "column" as const,
  },
  bannerImageContainer: {
    height: "120px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    position: "relative" as const,
  },
  bannerImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
  },
  videoPlaceholder: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFF8E1",
    color: "#E1A731",
    fontSize: "20px",
    fontWeight: "bold",
  },
  bannerActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 10px",
    backgroundColor: "#FFF8E1",
  },
  editDeleteButtons: {
    display: "flex",
    gap: "8px",
  },
  editButton: {
    color: "#E1A731",
    padding: "4px",
    minWidth: "auto",
    "&:hover": {
      backgroundColor: "rgba(225, 167, 49, 0.1)",
    },
  },
  trashButton: {
    color: "#EF4444",
    padding: "4px",
    minWidth: "auto",
    "&:hover": {
      backgroundColor: "rgba(239, 68, 68, 0.1)",
    },
  },
  arrowButtons: {
    display: "flex",
    gap: "6px",
  },
  arrowButton: {
    backgroundColor: "#E1A731",
    color: "white",
    padding: "4px",
    borderRadius: "4px",
    minWidth: "28px",
    width: "28px",
    height: "28px",
    "&:hover": {
      backgroundColor: "#D4A353",
    },
    "&.Mui-disabled": {
      backgroundColor: "#E0E0E0",
      color: "#9E9E9E",
    },
  },
  uploadIcon: {
    width: "40px",
    height: "40px",
    marginBottom: "10px",
    color: "#E1A731",
    [theme.breakpoints.down("sm")]: {
      width: "28px",
      height: "28px",
      marginBottom: "6px",
    },
  },
  uploadText: {
    color: "#E1A731",
    fontSize: "20px",
    fontWeight: 600,
    fontFamily: "DM Sans, sans-serif",
    [theme.breakpoints.down("sm")]: {
      fontSize: "12px",
      lineHeight: 1.3,
      textAlign: "center" as const,
    },
  },
  buttonContainer: {
    display: "flex",
    marginLeft: "30px",
    gap: "20px",
    justifyContent: "flex-start",
    padding: "0px 30px 30px 30px",
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      marginLeft: "0",
      padding: "0px 0px 16px 0px",
      flexDirection: "column",
    },
  },
  goldenButton: {
    backgroundColor: "#E1A731",
    color: "white",
    padding: "12px 32px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "bold",
    textTransform: "none",
    minWidth: "200px",
    "&:hover": {
      backgroundColor: "#D49820",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      minWidth: "unset",
    },
  },
  helpIcon: {
    width: "24px",
    height: "24px",
  },
  previewButton: {
    backgroundColor: "#333",
    color: "white",
    fontSize: "16px",
    fontWeight: "bold",
    borderRadius: "25px",
    padding: "12px 40px",
    textTransform: "none",
    marginTop: "20px",
    "&:hover": {
      backgroundColor: "#444",
    },
  },
  previewDialogPaper: {
    borderRadius: "16px",
    padding: "20px",
    maxWidth: "800px",
    width: "90%",
    position: "relative",
  },
  dialogCloseButton: {
    position: "absolute",
    right: "10px",
    top: "10px",
    backgroundColor: "#EF4444",
    color: "white",
    width: "32px",
    height: "32px",
    "&:hover": {
      backgroundColor: "#DC2626",
    },
  },
  inactiveBannerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "16px",
    marginBottom: "24px",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr 1fr",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr 1fr",
      gap: "12px",
    },
  },
  goldPillHeader: {
    display: "inline-flex",
    alignItems: "center",
    padding: "10px 24px",
    backgroundColor: "#E1A731",
    borderRadius: "30px",
    position: "relative" as const,
    top: "-18px",
    left: "30px",
    [theme.breakpoints.down("sm")]: {
      left: "12px",
      padding: "8px 16px",
    },
  },
  responsiveButton: {
    [theme.breakpoints.down("sm")]: {
      padding: "12px 32px !important",
      fontSize: "14px !important",
      width: "100%",
      maxWidth: "300px",
    },
  },
  videoButtonRow: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    flexWrap: "wrap" as const,
    margin: "0 24px 16px",
    [theme.breakpoints.down("sm")]: {
      margin: "0 12px 12px",
      flexDirection: "column",
      alignItems: "stretch",
      gap: "8px",
      width: "auto",
      "& > div": { width: "100%" },
      "& > div > a, & > div > a > button, & > div > button, & > button, & > a": {
        width: "100%",
        marginTop: 0,
      },
    },
  },
}));

const ManageFeaturedMemberBanners = (): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const history = useHistory();
  const chamberMemberInfo = useGetChamberMemberFromUserIdInAuth();
  const isMemberProfileAndPerksBannersActive = useIsMemberProfileAndPerksBannersActiveForChamberMember({
    activeMemberProfileAndPerksBannersSettings: chamberMemberInfo?.activeMemberProfileAndPerksBannersSettings,
  });

  // Banner Modal State
  const [bannerModalOpen, setBannerModalOpen] = useState(false);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [currentPerkIndex, setCurrentPerkIndex] = useState(-1); // -1 for main profile banners

  // Delete Confirmation State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number>(-1);
  const [pendingDeletePerkIndex, setPendingDeletePerkIndex] = useState<number>(-1);

  // Preview Dialog State
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewPerkIndex, setPreviewPerkIndex] = useState(-1);

  // Banner type definition
  type BannerData = {
    image: string | null;
    videoUrl: string | null;
    redirectUrl: string | null;
  };

  // Perk type definition
  type PerkData = {
    title: string;
    banners: BannerData[];
  };

  // Main profile banners state
  const [mainBannersData, setMainBannersData] = useState<BannerData[]>([
    { image: null, videoUrl: null, redirectUrl: null },
    { image: null, videoUrl: null, redirectUrl: null },
    { image: null, videoUrl: null, redirectUrl: null },
    { image: null, videoUrl: null, redirectUrl: null },
  ]);
  const [saving, setSaving] = useState(false);

  const chamberMemberId = chamberMemberInfo?.id || "";

  const { data: memberData } = useGetChamberMemberQuery({
    variables: { chamberMemberId },
    skip: !chamberMemberId,
    fetchPolicy: "network-only",
  });

  const [updateChamberMember] = useUpdateChamberMemberMutation();

  const memberRecord: any = useMemo(
    () => memberData?.getChamberMember,
    [memberData]
  );

  // Load existing banners from the DB on mount / when member data changes
  useEffect(() => {
    const existing: any[] = memberRecord?.banner || [];
    if (!existing || existing.length === 0) return;
    const slots: BannerData[] = [
      { image: null, videoUrl: null, redirectUrl: null },
      { image: null, videoUrl: null, redirectUrl: null },
      { image: null, videoUrl: null, redirectUrl: null },
      { image: null, videoUrl: null, redirectUrl: null },
    ];
    existing.forEach((b: any) => {
      const pos = typeof b.position === "number" ? b.position : -1;
      if (pos < 0 || pos > 3) return;
      if (b.contentType === BannerContentTypeEnum.Image) {
        slots[pos] = { image: b.contentUrl, videoUrl: null, redirectUrl: b.imageLink || null };
      } else if (b.contentType === BannerContentTypeEnum.Video) {
        slots[pos] = { image: null, videoUrl: b.contentUrl, redirectUrl: b.imageLink || null };
      }
    });
    setMainBannersData(slots);
  }, [memberRecord]);

  const handleUpdateMainBanners = async () => {
    if (!memberRecord || !chamberMemberId) return;
    setSaving(true);
    try {
      const bannersToSave = mainBannersData
        .map((b, i) => {
          if (b.image) {
            return {
              contentUrl: b.image,
              imageLink: b.redirectUrl || "",
              contentType: BannerContentTypeEnum.Image,
              position: i,
            };
          }
          if (b.videoUrl) {
            return {
              contentUrl: b.videoUrl,
              imageLink: b.redirectUrl || "",
              contentType: BannerContentTypeEnum.Video,
              position: i,
            };
          }
          return null;
        })
        .filter((b): b is NonNullable<typeof b> => b !== null);

      const mutationInput = omitDeep(
        {
          id: memberRecord.id,
          chamberId: memberRecord.chamberId,
          categoryId: memberRecord.categoryId,
          name: memberRecord.name,
          description: memberRecord.description,
          website: memberRecord.website,
          facebook: memberRecord.facebook,
          instagram: memberRecord.instagram,
          threads: memberRecord.threads,
          linkedin: memberRecord.linkedin,
          twitter: memberRecord.twitter,
          tiktok: memberRecord.tiktok,
          youtube: memberRecord.youtube,
          googleReviewLink: memberRecord.googleReviewLink,
          address: {
            city: memberRecord.address?.city || "",
            postalCode: memberRecord.address?.postalCode || "",
            province: memberRecord.address?.province || "",
            streetAddress: memberRecord.address?.streetAddress || "",
          },
          phoneNumber: memberRecord.phoneNumber,
          contactName: memberRecord.contactName,
          email: memberRecord.email,
          logo: memberRecord.logo,
          showInApp: memberRecord.showInApp,
          customContacts: (memberRecord.customContacts || []).map((c: any) => ({
            id: c.id,
            name: c.name,
            email: c.email,
            jobTitle: c.jobTitle,
            phoneNumber: c.phoneNumber,
          })),
          adminConsoleContactName: memberRecord.adminConsoleContactName,
          banner: bannersToSave,
        },
        "__typename"
      );

      await updateChamberMember({
        variables: { id: chamberMemberId, input: mutationInput as any },
        refetchQueries: ["getChamberMember", "getAllChamberMembers"],
        awaitRefetchQueries: true,
      });

      toast.success(
        enumStrings.toastMessages?.profileBannersUpdated || "Banners updated successfully"
      );
    } catch (error) {
      console.error(error);
      toast.error(
        enumStrings.toastMessages?.failedToUpdateProfileBanners || "Failed to update banners"
      );
    } finally {
      setSaving(false);
    }
  };

  // Perks state (can have up to 5 perks) - starts with 1 perk by default
  const [perks, setPerks] = useState<PerkData[]>([
    {
      title: "Perk Title 1",
      banners: [
        { image: null, videoUrl: null, redirectUrl: null },
        { image: null, videoUrl: null, redirectUrl: null },
        { image: null, videoUrl: null, redirectUrl: null },
        { image: null, videoUrl: null, redirectUrl: null },
      ],
    },
  ]);

  // Handler to navigate to create a new perk
  const handleCreatePerk = () => {
    history.push(RedirectUri.createDiscountChamberMemberAdmin);
  };

  // Handler to open banner modal
  const handleOpenBannerModal = (bannerIndex: number, perkIndex: number = -1) => {
    setCurrentBannerIndex(bannerIndex);
    setCurrentPerkIndex(perkIndex);
    setBannerModalOpen(true);
  };

  // Handler to save banner
  const handleSaveBanner = (data: BannerData) => {
    if (currentPerkIndex === -1) {
      // Main profile banners
      const newBannersData = [...mainBannersData];
      newBannersData[currentBannerIndex - 1] = data;
      setMainBannersData(newBannersData);
    } else {
      // Perk banners
      const newPerks = [...perks];
      newPerks[currentPerkIndex].banners[currentBannerIndex - 1] = data;
      setPerks(newPerks);
    }
    setBannerModalOpen(false);
  };

  // Handler to move banner
  const handleMoveBanner = (index: number, direction: 'left' | 'right', perkIndex: number = -1) => {
    const targetIndex = direction === 'left' ? index - 1 : index + 1;

    if (perkIndex === -1) {
      const newBannersData = [...mainBannersData];
      if (targetIndex >= 0 && targetIndex < newBannersData.length) {
        const temp = newBannersData[index];
        newBannersData[index] = newBannersData[targetIndex];
        newBannersData[targetIndex] = temp;
        setMainBannersData(newBannersData);
      }
    } else {
      const newPerks = [...perks];
      const banners = newPerks[perkIndex].banners;
      if (targetIndex >= 0 && targetIndex < banners.length) {
        const temp = banners[index];
        banners[index] = banners[targetIndex];
        banners[targetIndex] = temp;
        setPerks(newPerks);
      }
    }
  };

  // Handler to delete banner
  const handleDeleteBanner = (index: number, perkIndex: number = -1) => {
    setPendingDeleteIndex(index);
    setPendingDeletePerkIndex(perkIndex);
    setDeleteModalOpen(true);
  };

  const confirmDeleteBanner = () => {
    if (pendingDeletePerkIndex === -1) {
      const newBannersData = [...mainBannersData];
      newBannersData[pendingDeleteIndex] = { image: null, videoUrl: null, redirectUrl: null };
      setMainBannersData(newBannersData);
    } else {
      const newPerks = [...perks];
      newPerks[pendingDeletePerkIndex].banners[pendingDeleteIndex] = { image: null, videoUrl: null, redirectUrl: null };
      setPerks(newPerks);
    }
    setDeleteModalOpen(false);
    setPendingDeleteIndex(-1);
    setPendingDeletePerkIndex(-1);
  };

  // Get banners that have content for preview
  const getActiveBanners = (perkIndex: number = -1) => {
    const banners = perkIndex === -1 ? mainBannersData : perks[perkIndex]?.banners || [];
    return banners.filter(b => b.image || b.videoUrl);
  };

  const activeBanners = getActiveBanners(previewPerkIndex);

  const handleOpenPreview = (perkIndex: number = -1) => {
    const banners = getActiveBanners(perkIndex);
    if (banners.length > 0) {
      setPreviewPerkIndex(perkIndex);
      setPreviewIndex(0);
      setPreviewDialogOpen(true);
    }
  };

  const handlePreviewNext = () => {
    setPreviewIndex((prev) => (prev + 1) % activeBanners.length);
  };

  const handlePreviewPrev = () => {
    setPreviewIndex((prev) => (prev - 1 + activeBanners.length) % activeBanners.length);
  };

  // Get current banner data for modal
  const getCurrentBannerData = () => {
    if (currentPerkIndex === -1) {
      return mainBannersData[currentBannerIndex - 1] || { image: null, videoUrl: null, redirectUrl: null };
    }
    return perks[currentPerkIndex]?.banners[currentBannerIndex - 1] || { image: null, videoUrl: null, redirectUrl: null };
  };

  if (!isMemberProfileAndPerksBannersActive) {
    return (
      <div className={classes.root}>
        <div className={classes.titleRow}>
          <h1 className={classes.title}>Manage Featured Member Banners</h1>
        </div>
        <div className={classes.videoButtonRow}>
          <VideoTutorialButton
            tutorialUrl="https://share.synthesia.io/embeds/videos/2b53e93e-018e-4d54-9311-19f78ebacd9a"
          />
          <ExportButton
            customOnClick={() => openInstructionPdf("Activate Featured Member Profile & Perks Banners.pdf")}
            disablePadding
            icon={<InfoOutlinedIcon style={{ color: theme.palette.primary.main, marginRight: "10px", width: "24px", height: "24px" }} />}
            label={enumStrings.manageMembersPage.viewInstructionsTab}
          />
        </div>
        <div className={classes.content}>
          <div style={{ position: "relative" }}>
            {/* Gold Pill Header */}
            <div className={classes.goldPillHeader}>
              <span
                style={{
                  fontFamily: "DM Sans, sans-serif",
                  fontWeight: 700,
                  fontSize: "18px",
                  color: "#FFFFFF",
                }}
              >
                {enumStrings.createMemberForm.featuredMemberBannersTitle}
              </span>
            </div>

            <div style={{ padding: "clamp(12px, 4vw, 40px)" }}>
              {/* Description */}
              <p
                style={{
                  fontFamily: "DM Sans, sans-serif",
                  fontSize: "16px",
                  fontWeight: 400,
                  color: "#000000",
                  margin: "0 0 16px 0",
                  lineHeight: "150%",
                }}
              >
                {enumStrings.createMemberForm.featuredMemberBannersDesc}
              </p>

              {/* Not upgraded text */}
              <p
                style={{
                  fontFamily: "DM Sans, sans-serif",
                  fontSize: "16px",
                  fontWeight: 400,
                  color: "#000000",
                  margin: "0 0 16px 0",
                  lineHeight: "150%",
                  textAlign: "center",
                }}
              >
                {enumStrings.createMemberForm.featuredMemberNotUpgraded
                  .split("{notUpgraded}")[0]}
                <span style={{ fontWeight: 700 }}>not upgraded</span>
                {enumStrings.createMemberForm.featuredMemberNotUpgraded
                  .split("{notUpgraded}")[1]
                  ?.split("{clickHere}")[0]}
                <span
                  style={{
                    color: "#E1A731",
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  click here
                </span>
                {enumStrings.createMemberForm.featuredMemberNotUpgraded
                  .split("{clickHere}")[1]}
              </p>

              <FeaturedMemberBannerGuidance
                style={{
                  fontFamily: "DM Sans, sans-serif",
                  fontSize: "16px",
                  fontWeight: 400,
                  color: "#000000",
                  margin: "0 0 24px 0",
                  lineHeight: "150%",
                  textAlign: "center",
                }}
              />

              {/* Banner placeholder boxes */}
              <div className={classes.inactiveBannerGrid}>
                {[1, 2, 3, 4].map((bannerNum) => (
                  <div key={bannerNum}>
                    <div
                      style={{
                        textAlign: "center",
                        marginBottom: "10px",
                        fontWeight: 700,
                        color: "#000000",
                        fontSize: "16px",
                        fontFamily: "DM Sans, sans-serif",
                      }}
                    >
                      Banner {bannerNum}
                    </div>
                    <div
                      style={{
                        backgroundColor: "#9E9E9E",
                        borderRadius: "10px",
                        height: "170px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "default",
                      }}
                    >
                      <svg
                        width="40"
                        height="40"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9 16V10H5L12 3L19 10H15V16H9ZM5 20V18H19V20H5Z"
                          fill="#FFFFFF"
                        />
                      </svg>
                      <span
                        style={{
                          fontFamily: "DM Sans, sans-serif",
                          fontSize: "18px",
                          fontWeight: 600,
                          color: "#FFFFFF",
                          marginTop: "8px",
                        }}
                      >
                        {enumStrings.createMemberForm.uploadBannerLabel}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer text */}
              <p
                style={{
                  fontFamily: "DM Sans, sans-serif",
                  fontSize: "14px",
                  fontWeight: 400,
                  color: "#000000",
                  textAlign: "center",
                  margin: "0 0 24px 0",
                  lineHeight: "150%",
                }}
              >
                {enumStrings.createMemberForm.bannerPreviewFooter
                  .split("{update}")[0]}
                <span style={{ fontWeight: 700 }}>UPDATE</span>
                {enumStrings.createMemberForm.bannerPreviewFooter
                  .split("{update}")[1]}
              </p>

              {/* Preview Banners Button */}
              <div style={{ textAlign: "center", marginBottom: "40px" }}>
                <Button
                  variant="contained"
                  disabled
                  className={classes.responsiveButton}
                  style={{
                    backgroundColor: "#000000",
                    color: "#FFFFFF",
                    fontFamily: "DM Sans, sans-serif",
                    fontWeight: 700,
                    fontSize: "16px",
                    textTransform: "uppercase",
                    padding: "12px 48px",
                    borderRadius: "30px",
                    boxShadow: "none",
                    opacity: 0.6,
                  }}
                >
                  {enumStrings.createMemberForm.previewBannersButton}
                </Button>
              </div>

              {/* UPDATE Button */}
              <div style={{ textAlign: "center" }}>
                <Button
                  variant="contained"
                  disabled
                  className={classes.responsiveButton}
                  style={{
                    backgroundColor: "#E1A731",
                    color: "#FFFFFF",
                    fontFamily: "DM Sans, sans-serif",
                    fontWeight: 700,
                    fontSize: "18px",
                    textTransform: "uppercase",
                    padding: "14px 64px",
                    borderRadius: "30px",
                    boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
                    opacity: 0.6,
                  }}
                >
                  UPDATE
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.titleRow}>
        <h1 className={classes.title}>Manage Featured Member Banners</h1>
        <div className={classes.headerIcons}>
          <div className={classes.headerIcon}>
            <img src={HelpIcon} alt="Help" className={classes.helpIcon} />
          </div>
          <div className={classes.headerIcon}>
            <img src={LanguageIcon} alt="Language" className={classes.helpIcon} />
          </div>
          <div className={classes.headerIcon}>
            <img src={LogoutIcon} alt="Logout" className={classes.helpIcon} />
          </div>
        </div>
      </div>
      <div className={classes.videoButtonRow}>
        <VideoTutorialButton
          tutorialUrl="https://share.synthesia.io/embeds/videos/2b53e93e-018e-4d54-9311-19f78ebacd9a"
        />
        <ExportButton
          customOnClick={() => openInstructionPdf("Activate Featured Member Profile & Perks Banners.pdf")}
          disablePadding
          icon={<InfoOutlinedIcon style={{ color: theme.palette.primary.main, marginRight: "10px", width: "24px", height: "24px" }} />}
          label={enumStrings.manageMembersPage.viewInstructionsTab}
        />
      </div>

      <div className={classes.description}>
        <p>
          Upload up to 4 interchangeable images and video banners to showcase your company profile.
        </p>
        <p>
          Create up to 5 perks and upload up to 4 interchangeable images and video banners to showcase each perk.
        </p>
      </div>

      <div className={classes.content}>
        {/* Featured Member Profile Banners Section */}
        <div className={classes.sectionWrapper}>
          <div className={classes.formSection}>
            <div className={classes.sectionHeader}>
              <p className={classes.sectionTitle}>Featured Member Profile Banners</p>
            </div>
            <p className={classes.sectionHeader2} style={{ margin: "0 0 8px 0" }}>
              Upload up to 4 image or video banners to promote your profile.
            </p>
            <FeaturedMemberBannerGuidance className={classes.sectionHeader2} style={{ margin: "0 0 25px 0" }} />

            <div className={classes.uploadGrid}>
              {mainBannersData.map((banner, bannerIndex) => (
                <div key={bannerIndex}>
                  <div style={{ textAlign: "center", marginBottom: "10px", fontWeight: "bold", color: "#E1A731", fontSize: "16px" }}>
                    Banner {bannerIndex + 1}
                  </div>
                  {banner.image || banner.videoUrl ? (
                    <div className={classes.bannerCard}>
                      <div className={classes.bannerImageContainer}>
                        {banner.image ? (
                          <img
                            src={banner.image}
                            alt={`Banner ${bannerIndex + 1}`}
                            className={classes.bannerImage}
                          />
                        ) : (
                          <div className={classes.videoPlaceholder}>Video</div>
                        )}
                      </div>
                      <div className={classes.bannerActions}>
                        <div className={classes.editDeleteButtons}>
                          <IconButton
                            size="small"
                            className={classes.editButton}
                            onClick={() => handleOpenBannerModal(bannerIndex + 1, -1)}
                          >
                            <img src={EditOrangeIcon} alt="Edit" style={{ width: "20px", height: "20px" }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            className={classes.trashButton}
                            onClick={() => handleDeleteBanner(bannerIndex, -1)}
                          >
                            <img src={TrashIcon} alt="Delete" style={{ width: "20px", height: "20px" }} />
                          </IconButton>
                        </div>
                        <div className={classes.arrowButtons}>
                          <IconButton
                            size="small"
                            className={classes.arrowButton}
                            onClick={() => handleMoveBanner(bannerIndex, "left", -1)}
                            disabled={bannerIndex === 0}
                          >
                            <ArrowBackIosIcon style={{ fontSize: "14px", marginLeft: "4px" }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            className={classes.arrowButton}
                            onClick={() => handleMoveBanner(bannerIndex, "right", -1)}
                            disabled={bannerIndex === mainBannersData.length - 1}
                          >
                            <ArrowForwardIosIcon style={{ fontSize: "14px" }} />
                          </IconButton>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      onClick={() => handleOpenBannerModal(bannerIndex + 1, -1)}
                      style={{
                        backgroundColor: "#9E9E9E",
                        borderRadius: "10px",
                        height: "170px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                    >
                      <img src={UploadIcon} alt="Upload" style={{ width: 40, height: 40, filter: "brightness(0) invert(1)" }} />
                      <span style={{ fontFamily: "DM Sans, sans-serif", fontSize: "18px", fontWeight: 600, color: "#FFFFFF", marginTop: "8px" }}>
                        Upload
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div style={{ textAlign: "center", marginTop: "20px" }}>
              <Button
                variant="contained"
                onClick={handleUpdateMainBanners}
                disabled={saving}
                className={classes.responsiveButton}
                style={{
                  backgroundColor: "#E1A731",
                  color: "#FFFFFF",
                  fontFamily: "DM Sans, sans-serif",
                  fontWeight: 700,
                  fontSize: "18px",
                  textTransform: "uppercase",
                  padding: "14px 64px",
                  borderRadius: "30px",
                  boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
                }}
              >
                {saving ? <CircularProgress size={24} style={{ color: "#FFFFFF" }} /> : "UPDATE"}
              </Button>
            </div>
          </div>
        </div>

        {/* Perk Sections */}
        {perks.map((perk, perkIndex) => (
          <div key={perkIndex} className={classes.sectionWrapper}>
            <div className={classes.formSection}>
              <div className={classes.sectionHeader}>
                <p className={classes.sectionTitle}>Featured Member Perk Banners</p>
              </div>
              <img
                src={InformationIcon}
                alt="Info"
                className={classes.infoIcon}
              />
              <p className={classes.sectionHelperText} style={{ margin: "0 0 15px 0" }}>
                Images and video banners uploaded here will appear on your perk page.
              </p>

              <p className={classes.sectionHeader2} style={{ margin: "0 0 8px 0" }}>
                Upload up to 4 image or video banners to promote this perk.
              </p>
              <FeaturedMemberBannerGuidance className={classes.sectionHeader2} style={{ margin: "0 0 25px 0" }} />

              <div className={classes.uploadGrid}>
                {perk.banners.map((banner, bannerIndex) => (
                  <div key={bannerIndex}>
                    <div style={{ textAlign: "center", marginBottom: "10px", fontWeight: "bold", color: "#E1A731", fontSize: "16px" }}>
                      Banner {bannerIndex + 1}
                    </div>
                    {banner.image || banner.videoUrl ? (
                      <div className={classes.bannerCard}>
                        <div className={classes.bannerImageContainer}>
                          {banner.image ? (
                            <img
                              src={banner.image}
                              alt={`Banner ${bannerIndex + 1}`}
                              className={classes.bannerImage}
                            />
                          ) : (
                            <div className={classes.videoPlaceholder}>
                              Video
                            </div>
                          )}
                        </div>
                        <div className={classes.bannerActions}>
                          <div className={classes.editDeleteButtons}>
                            <IconButton
                              size="small"
                              className={classes.editButton}
                              onClick={() => handleOpenBannerModal(bannerIndex + 1, perkIndex)}
                            >
                              <img src={EditOrangeIcon} alt="Edit" style={{ width: "20px", height: "20px" }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              className={classes.trashButton}
                              onClick={() => handleDeleteBanner(bannerIndex, perkIndex)}
                            >
                              <img src={TrashIcon} alt="Delete" style={{ width: "20px", height: "20px" }} />
                            </IconButton>
                          </div>
                          <div className={classes.arrowButtons}>
                            <IconButton
                              size="small"
                              className={classes.arrowButton}
                              onClick={() => handleMoveBanner(bannerIndex, 'left', perkIndex)}
                              disabled={bannerIndex === 0}
                            >
                              <ArrowBackIosIcon style={{ fontSize: "14px", marginLeft: "4px" }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              className={classes.arrowButton}
                              onClick={() => handleMoveBanner(bannerIndex, 'right', perkIndex)}
                              disabled={bannerIndex === perk.banners.length - 1}
                            >
                              <ArrowForwardIosIcon style={{ fontSize: "14px" }} />
                            </IconButton>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div
                        onClick={() => handleOpenBannerModal(bannerIndex + 1, perkIndex)}
                        className={classes.bannerCard}
                        style={{ cursor: "pointer", height: "170px", backgroundColor: "#FFFFFF" }}
                      >
                        <div style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          padding: "20px"
                        }}>
                          <img
                            src={UploadIcon}
                            alt="Upload"
                            style={{ width: "40px", height: "40px", marginBottom: "10px" }}
                          />
                          <p style={{
                            color: "#E1A731",
                            fontSize: "20px",
                            fontWeight: 600,
                            margin: 0,
                            fontFamily: "DM Sans, sans-serif",
                          }}>
                            Upload Banner {bannerIndex + 1}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Preview Banners Button - only show if at least one image or video is selected */}
              {getActiveBanners(perkIndex).length > 0 && (
                <div style={{ textAlign: "center", marginTop: "30px" }}>
                  <Button
                    className={classes.previewButton}
                    onClick={() => handleOpenPreview(perkIndex)}
                  >
                    Preview Banners
                  </Button>
                </div>
              )}

            </div>
            {/* Perk Title Buttons */}
            <div className={classes.buttonContainer}>
              <Button className={classes.goldenButton}>
                Perk Title {perkIndex + 1}
              </Button>
              {perks.length < 5 && (
                <Button className={classes.goldenButton} onClick={handleCreatePerk}>
                  Create a perk
                </Button>
              )}
            </div>
          </div>

        ))}



        {/* Banner Upload Modal */}
        <BannerUploadModal
          open={bannerModalOpen}
          onClose={() => setBannerModalOpen(false)}
          bannerIndex={currentBannerIndex}
          useFeaturedMemberGuidance
          initialData={getCurrentBannerData()}
          onSave={handleSaveBanner}
        />

        {/* Delete Banner Confirmation Modal */}
        <DeleteBannerModal
          open={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setPendingDeleteIndex(-1);
            setPendingDeletePerkIndex(-1);
          }}
          onDelete={confirmDeleteBanner}
        />

        {/* Preview Dialog */}
        <Dialog
          open={previewDialogOpen}
          onClose={() => setPreviewDialogOpen(false)}
          classes={{ paper: classes.previewDialogPaper }}
        >
          <IconButton
            className={classes.dialogCloseButton}
            onClick={() => setPreviewDialogOpen(false)}
          >
            <CloseIcon />
          </IconButton>
          <div style={{ textAlign: "center", padding: "20px" }}>
            <h2 style={{ color: "#E1A731", marginBottom: "20px" }}>Banner Preview</h2>
            {activeBanners.length > 0 && (
              <>
                {activeBanners[previewIndex]?.image ? (
                  <img
                    src={activeBanners[previewIndex].image!}
                    alt="Preview"
                    style={{ maxWidth: "100%", maxHeight: "400px", borderRadius: "8px" }}
                  />
                ) : (
                  <div style={{
                    width: "100%",
                    height: "300px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#FFF8E1",
                    borderRadius: "8px",
                    color: "#E1A731",
                    fontSize: "24px",
                    fontWeight: "bold"
                  }}>
                    Video: {activeBanners[previewIndex]?.videoUrl}
                  </div>
                )}
                {activeBanners.length > 1 && (
                  <div style={{ display: "flex", justifyContent: "center", gap: "20px", marginTop: "20px" }}>
                    <IconButton onClick={handlePreviewPrev} style={{ backgroundColor: "#E1A731", color: "white" }}>
                      <ArrowBackIosIcon style={{ marginLeft: "4px" }} />
                    </IconButton>
                    <span style={{ alignSelf: "center", fontSize: "16px" }}>
                      {previewIndex + 1} / {activeBanners.length}
                    </span>
                    <IconButton onClick={handlePreviewNext} style={{ backgroundColor: "#E1A731", color: "white" }}>
                      <ArrowForwardIosIcon />
                    </IconButton>
                  </div>
                )}
              </>
            )}
          </div>
        </Dialog>
      </div>
    </div>
  );
};

export default ManageFeaturedMemberBanners;
